import React, {useEffect, useRef, useState} from "react";
import {useStripe, useElements, CardElement} from "@stripe/react-stripe-js";
import type {Stripe, StripeElements, StripeCardElement} from "@stripe/stripe-js";
import {AxiosError} from "axios";

// Components
import InputsStripe from "../../Inputs/InputsStripe";

// Api
import {
    CreateSetupIntent,
    SetDefaultPaymentMethod,
} from "../../../Api/Stripe";

// Models
import {UserPaymentMethod} from "../../../Models/Interfaces/UserPaymentMethod";
import useFetchUserPaymentMethods from "../../../Hooks/User/useFetchUserPaymentMethods";
import {getCardBrandIcon} from "../../../Helpers/Currency/Currency";
import {capitalizeFirst} from "../../../Helpers/DataTypesGeneral/Strings";
import {getPaymentIntentClientAction} from "../../../Helpers/StripePaymentIntent";
import IconSpinLoader from "../../../Assets/SVGs/Icons/Loading/SpinLoader";
import ButtonPill from "../../Buttons/ButtonPill";
import IconCheckMarkHollow from "../../../Assets/SVGs/Icons/YesOrNo/CheckMarkHollow";
import ErrorMessage from "../../Text/ErrorMessage";

interface PaymentMethodProps {
    userId: string;
    getValidAccessToken: () => Promise<string | null>;
    onDone?: () => void;
    darkMode?: boolean;
    closePaymentMethodPopUp?: () => void;
    showUpdateForm: boolean;
    setShowUpdateForm: (show: boolean) => void;
    presentDefaultPM?: (defaultPM: UserPaymentMethod | null) => void;
    presentPaymentMethodsState?: (loading: boolean, error: string) => void;
}

interface PaymentRequiresActionResponse {
    error?: string;
    message?: string;
    client_secret?: string;
    invoice_id?: string;
    payment_intent_status?: string;
}

const PaymentMethod: React.FC<PaymentMethodProps> = (
    {
        userId,
        getValidAccessToken,
        onDone,
        darkMode = true,
        closePaymentMethodPopUp,
        showUpdateForm,
        setShowUpdateForm,
        presentDefaultPM = () => {},
        presentPaymentMethodsState = () => {}
    }
) => {
    const
        stripe = useStripe(),
        elements = useElements(),

        acRef = useRef<AbortController | null>(null),
        presentDefaultPMRef = useRef(presentDefaultPM),
        presentPaymentMethodsStateRef = useRef(presentPaymentMethodsState),
        getAC = () => {
            acRef.current?.abort();
            acRef.current = new AbortController();
            return acRef.current;
        },

        [busy, setBusy] = useState(false),
        [cardComplete, setCardComplete] = useState(false),
        [cCUpdateComplete, setCCUpdateComplete] = useState(false),
        [zip, setZip] = useState(""),
        [err, setErr] = useState<string | null>(null),

        {
            defaultPM,
            loading: paymentMethodsLoading,
            error: paymentMethodsError,
        } = useFetchUserPaymentMethods(
            userId,
            getValidAccessToken,
            showUpdateForm
        ),

        setFormBackToInfo = () => {
            setShowUpdateForm(false);
            setCCUpdateComplete(false);
        },

        handleCancel = () => {
            closePaymentMethodPopUp && closePaymentMethodPopUp();
            setShowUpdateForm(false);
        },

        confirmNewCard = async (
            stripeInst: Stripe,
            elementsInst: StripeElements,
            accessToken: string
        ): Promise<{ ok: boolean; pmId?: string; error?: string }> => {
            setErr(null);

            const cardEl = elementsInst.getElement(CardElement) as StripeCardElement | null;
            if (!cardEl) return {ok: false, error: "Payment element not found"};
            if (!cardComplete) return {ok: false, error: "Please complete your card details"};

            // 1) Server creates SetupIntent
            let clientSecret: string;
            try {
                const ac = getAC(),
                    {client_secret} = await CreateSetupIntent(userId, accessToken, ac);
                clientSecret = client_secret;
            } catch (e: any) {
                return {ok: false, error: e?.message || "Failed to create SetupIntent"};
            }

            // 2) Client confirms it
            const {setupIntent, error} = await stripeInst.confirmCardSetup(clientSecret, {
                payment_method: {
                    card: cardEl,
                    billing_details: {
                        address: zip ? {postal_code: zip} : undefined,
                    },
                },
            });

            if (error) {
                return {ok: false, error: error.message || "Card confirmation failed"};
            }

            const pmId = setupIntent?.payment_method as string | undefined;
            if (!pmId) return {ok: false, error: "No payment method returned"};
            return {ok: true, pmId};
        },

        onSubmit = async (e: React.FormEvent) => {

            e.preventDefault();
            if (busy) return;

            if (!stripe || !elements) {
                setErr("Payments not initialized. Reload and try again.");
                return;
            }

            setBusy(true);
            setErr(null);

            try {
                const accessToken = await getValidAccessToken();
                if (!accessToken) {
                    setErr("Your session has expired. Please log in and try again.");
                    return;
                }

                // A) Confirm new PM via SetupIntent
                const confirmed = await confirmNewCard(stripe, elements, accessToken);
                if (!confirmed.ok || !confirmed.pmId) {
                    setErr(confirmed.error || "Unable to confirm card");
                    setBusy(false);
                    return;
                }

                // B) Replace default (optionally detach old, optionally try to pay latest open invoice)
                // SetupIntent confirmation can remain open for several minutes, so
                // refresh again instead of reusing the token from before 3DS.
                const mutationToken = await getValidAccessToken();
                if (!mutationToken) {
                    setErr("Your session has expired. Please log in and try again.");
                    return;
                }
                const ac = getAC();
                try {
                    await SetDefaultPaymentMethod(userId, confirmed.pmId, true, mutationToken, ac);
                } catch (e: unknown) {
                    const action = e instanceof AxiosError
                        ? e.response?.data as PaymentRequiresActionResponse | undefined
                        : undefined;
                    if (
                        e instanceof AxiosError &&
                        e.response?.status === 402 &&
                        action?.error === "payment_requires_action" &&
                        action.client_secret
                    ) {
                        const clientAction = getPaymentIntentClientAction(
                            action.payment_intent_status,
                            true
                        );
                        if (
                            clientAction !== "confirm_existing" &&
                            clientAction !== "confirm_with_payment_method"
                        ) {
                            throw new Error("Invoice payment cannot be confirmed in its current state.");
                        }
                        const {error, paymentIntent} = clientAction === "confirm_with_payment_method"
                            ? await stripe.confirmCardPayment(
                                action.client_secret,
                                {payment_method: confirmed.pmId}
                            )
                            : await stripe.confirmCardPayment(action.client_secret);
                        if (error) throw error;
                        if (paymentIntent?.status !== "succeeded") {
                            throw new Error("Invoice payment is still processing. Please check your account before retrying.");
                        }

                        // The interactive bank step may outlive the access token.
                        // Finalize defaults and detach the old cards without paying
                        // the now-completed invoice a second time.
                        const finalizeToken = await getValidAccessToken();
                        if (!finalizeToken) {
                            throw new Error("Your session has expired. Please log in and try again.");
                        }
                        await SetDefaultPaymentMethod(
                            userId,
                            confirmed.pmId,
                            true,
                            finalizeToken,
                            getAC(),
                            false
                        );
                    } else {
                        throw e;
                    }
                }

                setCCUpdateComplete(true);
                setTimeout(() => setFormBackToInfo(), 2000);
                onDone?.();
            } catch (e: any) {
                const message =
                    e instanceof AxiosError
                        ? e.response?.data?.message || e.message
                        : e?.message || "Something went wrong";
                setErr(message);
            } finally {
                setBusy(false);
            }
        };

    presentDefaultPMRef.current = presentDefaultPM;
    presentPaymentMethodsStateRef.current = presentPaymentMethodsState;

    useEffect(() => {
        presentDefaultPMRef.current(defaultPM);
    }, [defaultPM]);

    useEffect(() => {
        presentPaymentMethodsStateRef.current(paymentMethodsLoading, paymentMethodsError);
    }, [paymentMethodsLoading, paymentMethodsError]);

    return (
        <div className={`form form--payment-method ${darkMode ? "is-dark" : "is-light"}`}>

            {showUpdateForm ?
                (
                    <div className="payment-method__new-card">
                        <h3>Update Card</h3>

                        <InputsStripe
                            name="CreditCard"
                            value=""
                            isDarkMode={darkMode}
                            hasError={!!err}
                            setCardComplete={setCardComplete}
                            updateZipCode={setZip}
                        />

                        {err && <ErrorMessage errorMessage={err} />}
                    </div>
                )
                :
                (
                    <div className="payment-method__current-card">
                        <h3>Card Information</h3>

                        <div className="payment-method__card-info">
                            {
                                paymentMethodsLoading ? IconSpinLoader({className: 'icon--spinner'})
                                    :
                                    (
                                        <>
                                            {defaultPM?.brand && getCardBrandIcon(defaultPM.brand)}
                                            {
                                                defaultPM ? (
                                                    <div className="payment-method__card">
                                                        <p>
                                                            {defaultPM.brand ? capitalizeFirst(defaultPM.brand) : "CARD"} ending
                                                            in: {defaultPM.last4}

                                                        </p>

                                                        {
                                                            defaultPM.exp_month && defaultPM.exp_year ? (
                                                                <p className="payment-method__expiry">
                                                                    EXPIRY <span>{String(defaultPM.exp_month).padStart(2, "0")}/{String(defaultPM.exp_year).slice(-2)}</span>
                                                                </p>
                                                            ) : null
                                                        }
                                                    </div>
                                                ) : !paymentMethodsError ? <p>No default card on file.</p> : null
                                            }
                                        </>
                                    )
                            }
                        </div>
                    </div>
                )
            }

            {paymentMethodsError && <ErrorMessage errorMessage={paymentMethodsError} />}

            <div className="payment-method__buttons">
                <>
                    {(closePaymentMethodPopUp !== undefined || showUpdateForm) && (
                        <ButtonPill
                            label="Cancel"
                            type="button"
                            onClick={handleCancel}
                        />
                    )}

                    {showUpdateForm ?
                        (
                            <ButtonPill
                                label={busy || cCUpdateComplete ? "" : "Update Card"}
                                className={`payment-method__submit ${busy ? "is-loading" : "is-not-loading"} ${cCUpdateComplete ? "has-success" : "has-no-success"}`}
                                type="button"
                                icon={cCUpdateComplete ? IconCheckMarkHollow() : IconSpinLoader({className: 'icon--spin-loader'})}
                                onClick={onSubmit}
                            />
                        )
                        :
                        (
                            <ButtonPill
                                label={!defaultPM ? "Add Payment Method" : "Change Card"}
                                type="button"
                                onClick={() => setShowUpdateForm(true)}
                            />
                        )
                    }
                </>
            </div>
        </div>
    );
};

export default PaymentMethod;
